import { DOMAIN } from '@/config/paths'

export function capitalizeFirstLetter(string) {
  return string ? string[0].toUpperCase() + string.slice(1) : ''
}

export function titleize(string) {
  return string.toLowerCase().replace(/(^|\s|-)\S/g, m => m.toUpperCase())
}

/**
 * Returns lower case string with spaces replaced by dashes.
 *
 * @param {String} string
 *
 * @returns fromatted string
 */
export function parameterize(string) {
  return string.replace(/\s+/g, '-').toLowerCase()
}

export function isString(string) {
  return typeof string === 'string'
}

export function isEmpty(string) {
  return !string || !/\S/.test(string)
}

export function makeHtmlSafe(str) {
  return str.replace(/[&<>"]/g, match => {
    const entities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
    }
    return entities[match]
  })
}

export function removeLeadingSpaces(string) {
  while (string.charAt(0) == ' ') {
    string = string.substring(1)
  }
  return string
}

/**
 * Make sure an internal link is using the current locale if the link is localized.
 *
 * @name resolveLinkLocale
 * @function
 * @param {string} link - Any url that is possibly an internal link.
 *
 * @example resolveLinkLocale('https://www.on-running.com/fr-ca/test')
 * @returns {string} https://www.on-running.com/en-ch/test (if current locale == 'en-ch')
 */
export function resolveLinkLocale(link) {
  const encodedDomain = DOMAIN.replace('.', '\\.')
  const regex = new RegExp(`^(https?://([\\w-]+)\\.${encodedDomain}/)([a-z]{2}-[a-z]{2}/)`)

  return link.replace(regex, (m, p1) => `${p1}${On.Store.state.page.locale}/`)
}

/**
 * Make sure that no line break leaves a single word by itself on the last line.
 *
 * Especially useful in French since there is always a space before some
 * punctuation marks (?!:;) meaning this function would prevent having a single
 * character on the last line.
 *
 * @name resolveLineBreakOfLastWord
 * @function
 * @param {string} paragraph - Any paragraph printed as HTML.
 * @param {object} params - Override default parameters with params object.
 * @param {number} params.minWordCount - Minimum amount of words that the paragraph needs to have in order to proceed.
 * @param {number} params.maxLengthOfLastWord - The length of the last word needs to be less than this param in order to proceed.
 * @param {number} params.maxLengthOfConcat - The total length of the last word together with the second last word needs to be less than this param in order to proceed.
 */
export function resolveLineBreakOfLastWord(paragraph, params = {}) {
  const defaultParams = {
    minWordCount: 4,
    maxLengthOfLastWord: 20,
    maxLengthOfConcat: 25,
  }
  const options = { ...defaultParams, ...params }
  const words = paragraph.split(/ +/)
  const lastWord = words[words.length - 1]
  const secondLastWord = words[words.length - 2] || ''
  const concatenatedWords = `${secondLastWord} ${lastWord}`

  const hasOneCharLongLastWord = lastWord.length === 1
  const meetParamCriteria =
    words.length > options.minWordCount &&
    lastWord.length < options.maxLengthOfLastWord &&
    concatenatedWords.length < options.maxLengthOfConcat

  if (hasOneCharLongLastWord || meetParamCriteria) {
    return (
      words.slice(0, words.length - 2).join(' ') +
      ` <span class="u-nobr">${secondLastWord} ${lastWord}</span>`
    )
  }
  return paragraph
}

/**
 * When the sentence breaks into two lines, this function will equalize the length of the
 * lines rounding it in favor of making the first line longer. If you use it, you have to make sure
 * that the sentence can only break into 2 lines, not three otherwise, you will end up with text
 * overflow.
 *
 * @name equalizeLineLengthOnBreak
 * @function
 * @param {string} sentence - Any sentence printed as HTML.
 */
export function equalizeLineLengthOnBreak(sentence, params = {}) {
  const defaultParams = {
    firstLineRatio: 0.5,
  }
  const options = { ...defaultParams, ...params }
  const halfLength = Math.round(sentence.length * options.firstLineRatio)
  const halfIndex = halfLength + sentence.substring(halfLength).match(/( +|$)/).index
  const firstLine = sentence.substring(0, halfIndex)
  const secondLine = sentence.substring(halfIndex)

  return `${firstLine} <span class="u-nobr">${secondLine}</span>`
}

export function escapeRegExp(string) {
  return string.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
}
